import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Loader,
  Timeline,
  type TimelineColorThemesKinds,
  type TimelineConnectorKinds,
  type TimelineMedallionKinds,
  Typography,
} from '@saviynt/design-system';

import { AUDIT_TRAIL_MAP } from '../../constants';
import { useInvertedLocalize } from '../../hooks';
import { globalActions } from '../../store/globalSlice';
import {
  type ChangeLogItem,
  type HistoryTimelineMappedData,
  type SodSidePanelHistoryData,
} from '../../types';

import ExpandableBlock from './ExpandableBlock';

import './SodSidePanelHistory.css';

/**
 * SodSidePanelHistory
 */
function SodSidePanelHistory({
  sodKey,
  isInDetailsPage,
}: {
  sodKey: string;
  isInDetailsPage: boolean;
}) {
  const l = useInvertedLocalize();

  const [data, setData] = useState<SodSidePanelHistoryData[] | null>(null);

  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [fetchHistory, { isLoading, isError, isFetching }] =
    globalActions.useLazyGetHistoryDetailsQuery();

  const handlFetchHistory = useCallback(
    async (payload: string) => {
      abortRef.current?.('New Search');

      try {
        const controller = fetchHistory(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error('Failed History API');
      }
    },
    [fetchHistory]
  );

  useEffect(() => {
    handlFetchHistory(sodKey)
      .then((response) => {
        const typedResponse = response as SodSidePanelHistoryData[];

        setData(typedResponse);
      })
      .catch(() => {});
  }, [sodKey, handlFetchHistory]);

  const mapData = useCallback(
    (
      dataToMap: SodSidePanelHistoryData[] | null
    ): HistoryTimelineMappedData[] => {
      if (!dataToMap) {
        return [];
      }

      return dataToMap.map((item) => {
        // Check if the auditEventType exists in AUDIT_TRAIL_MAP
        const { auditEventType, tags } = item;
        const currentBlock = AUDIT_TRAIL_MAP[auditEventType] as {
          colorTheme: TimelineColorThemesKinds;
          connectorKind: TimelineConnectorKinds;
          icon: string;
          medallionKind: TimelineMedallionKinds;
          text: string;
        };

        const mitigatingControlApplied = item.changeLog.filter(
          (
            it
          ): it is ChangeLogItem & {
            objects: { value: string }[][];
          } =>
            'objects' in it &&
            Array.isArray(it.objects) &&
            it.objects.length > 0
        );

        const postBlockContent = (
          <Box className='SodSidePanelHistory-postBlock'>
            {mitigatingControlApplied.length > 0 && (
              <ExpandableBlock items={mitigatingControlApplied[0].objects} />
            )}
          </Box>
        );

        // Currently this is required to protect UI break when there is no match in AUDIT_TRAIL_MAP
        // Remove this once all keys idetified from backend.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (currentBlock === undefined) {
          return {
            title: '',
            attributes: item.changeLog
              .filter((i) => i.key !== 'Mitigating Controls Applied')
              .map((i: { key: string; value: string }) => ({
                label: i.key,
                detail: i.value,
              })),
            connectorKind: 'past',
            medallionKind: 'filled',
            icon: '',
            colorTheme: 'positive',
            date: new Date(item.eventTimestamp),
            isTimeEnabled: isInDetailsPage,
            size: isInDetailsPage ? 'medium' : 'small',
            postBlockContent: isInDetailsPage ? postBlockContent : null,
          };
        }

        return {
          title:
            tags.length > 0 ? (
              <Box className='SodSidePanelHistory-title'>
                <Typography
                  kind='h3'
                  desktop='body2-bold-d'
                  mobile='body2-bold-d'
                  color='--color-foreground-secondary-bold'>
                  {currentBlock.text}
                </Typography>
                <Badge
                  kind='number'
                  number={Number(tags[0].value)}
                  color='secondary'
                  badgeStyle='outlined'
                />
              </Box>
            ) : (
              l(currentBlock.text)
            ),
          attributes: item.changeLog
            .filter((i) => i.key !== 'Mitigating Controls Applied')
            .map((i: { key: string; value: string }) => ({
              label: l(i.key),
              detail: i.value,
            })),
          connectorKind: currentBlock.connectorKind,
          medallionKind: currentBlock.medallionKind,
          icon: currentBlock.icon,
          colorTheme: currentBlock.colorTheme,
          date: new Date(item.eventTimestamp),
          isTimeEnabled: isInDetailsPage,
          size: isInDetailsPage ? 'medium' : 'small',
          postBlockContent: isInDetailsPage ? postBlockContent : null,
        };
      });
    },
    [l, isInDetailsPage]
  );

  const [timelineData, setTimelineData] = useState<HistoryTimelineMappedData[]>(
    []
  );

  useEffect(() => {
    const mappedData = mapData(data);

    setTimelineData(mappedData);
  }, [data, mapData]);

  if (isLoading || isFetching) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Loader kind='dots' format='inline' color='brand' />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Typography kind='body2'>
          {l('Error loading history details, please try again.')}
        </Typography>
        <Button
          type='button'
          kind='outlined'
          isEnabled
          onClick={() => {
            handlFetchHistory(sodKey)
              .then((response) => {
                const typedResponse = response as SodSidePanelHistoryData[];

                setData(typedResponse);
              })
              .catch(() => {});
          }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (data === null) {
    return null;
  }

  if (data.length === 0) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Typography kind='body2'>{l('No Records Found.')}</Typography>
      </Box>
    );
  }

  return <Timeline timelineBlocksData={timelineData} />;
}

export default SodSidePanelHistory;
